import axios from "axios";
import { useEffect, useState } from "react";
import LoadingPanel from "../../../components/common/LoadingPanel";
import useGlobalContext from "../../../hooks/useGlobalContext";
import {
  getClearGeneralRequest,
  getUserName,
} from "../../../common/EntitiesManagement";
import TableComponent from "../../../components/common/TableComponent";
import { Alert, Box, Button, Link } from "@mui/material";
import localize from "../../../common/localize";
import PagePagination from "../../../components/PagePagination";
import {
  getCurrentDays,
  getDaysToError,
  getDaysToWarning,
  generateNoDataValues,
  GetDateInDDMMYYYYFormat,
  PaginationLimit,
} from "../../../common/utils";
import ErrorMessage from "../../../components/common/ErrorMessage";
import ActionButtons from "../../../components/common/ActionButtons";
import WarningErrorStatus from "../../../components/common/WarningErrorStatus";
import { BackForeColor } from "../../../components/common/BackForeColor";
import RequestStatus from "../../../components/common/RequestStatus";
import { Add } from "@mui/icons-material";
import ModalBackground from "../../../components/common/ModalBackground";
import AddOrEditGeneralRequests from "./AddOrEditGeneralRequests";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";

export default function MyRequests({ filters }) {
  const {
    config,
    GENERAL_REQUESTS_ENDPOINT,
    pageIndexMyRequests,
    setPageIndexMyRequets,
    createEntity,
    updateEntity,
  } = useGlobalContext();
  const [isLoading, setIsLoading] = useState(false);
  const [myRequestsData, setMyRequestsData] = useState([]);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [requestModel, setRequestModel] = useState(getClearGeneralRequest());

  const getMyRequests = async () => {
    try {
      setIsLoading(true);

      const response = await axios.get(
        `${GENERAL_REQUESTS_ENDPOINT}/${getUserName()}`,
        {
          ...config,
          headers: {
            ...config.headers,
            pagination: pageIndexMyRequests,
            Filter: JSON.stringify(filters),
          },
        }
      );

      if (response?.data?.Ok && response?.data?.Data) {
        const pages = response?.data?.Count / PaginationLimit();
        setNumberOfPages(pages > 0 ? pages : 1);
        setMyRequestsData(response.data.Data);
      }
    } catch (exception) {
      setErrorMessage(exception.message);
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const addRequest = () => {
    setRequestModel(getClearGeneralRequest());
    setShowAddModal(true);
  };

  const createGeneralRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${GENERAL_REQUESTS_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const updateGeneralRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${GENERAL_REQUESTS_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const validateOperationResult = (result, isDelete) => {
    setRequestModel(getClearGeneralRequest());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    window.location.reload(false);
  };

  const disableSaveInformation = () =>
    requestModel.RequestTypeId <= 0 || requestModel.PersonalId <= 0;

  const showUI = () => {
    return (
      <Box className="flex flex-col gap-2 w-full">
        {showErrorModal && (
          <ErrorMessage
            message={errorMessage}
            title={localize.error_unexpected}
            showModal={showErrorModal}
            setShowModal={setShowErrorModal}
          />
        )}

        {showAddModal && (
          <ModalBackground
            isDrawerMode
            hasRequiredValues
            title={
              requestModel.Id > 0
                ? `${localize.edit_request} #${requestModel.Id}`
                : localize.new_request
            }
            showModal={showAddModal}
            setShowModal={setShowAddModal}
            onClickToCancel={() => {
              setRequestModel(getClearGeneralRequest());
              setShowAddModal(false);
            }}
            onClickToSubmit={() => {
              requestModel.Id > 0
                ? updateGeneralRequest(requestModel)
                : createGeneralRequest(requestModel);
              setShowAddModal(false);
            }}
            validators={disableSaveInformation()}
            Component={
              <AddOrEditGeneralRequests
                model={requestModel}
                setModel={setRequestModel}
                readOnly={requestModel?.Status > 1}
              />
            }
            hideSubmitButton={requestModel?.Status > 1}
            cancelButtonText={
              requestModel?.Status > 1 ? localize.close : localize.cancel
            }
          />
        )}

        <Box className="w-full flex gap-2 items-center">
          <Alert sx={{ width: "100%" }} severity="info">
            <label className="text-xs">
              {localize.general_requests_description}
            </label>
          </Alert>

          <Button
            size="medium"
            variant="contained"
            color="success"
            className="app_general_button_data app_success_button btn-sm "
            onClick={() => {
              addRequest();
            }}
          >
            <Add style={{ width: "16px", height: "16px" }} />
            <div className="mr-1" />
            {localize.add}
          </Button>
        </Box>

        <TableComponent
          firstColumnTitle={" "}
          noPadding
          pagination={
            <PagePagination
              pageIndex={pageIndexMyRequests}
              setPageIndex={setPageIndexMyRequets}
              dataArray={myRequestsData}
              customNumberOfPages={numberOfPages}
            />
          }
          columnTitles={[
            localize.actions,
            localize.type,
            localize.status,
            localize.event,
            localize.client,
            localize.assigned_to,
            localize.expected_attention_date,
            localize.creation_date,
          ]}
          results={
            myRequestsData?.length > 0
              ? myRequestsData.map((item) => ({
                  key: item.Id,
                  data: [
                    <WarningErrorStatus
                      currentValue={getCurrentDays(item)}
                      limitForWarning={getDaysToWarning(item)}
                      limitForError={getDaysToError(item)}
                      isSucceeded={item?.Status === 2}
                      isFailed={item?.Status === 3}
                    />,
                    <div>
                      <ActionButtons
                        onClickForEditButton={() => {
                          setRequestModel({
                            ...item,
                            PersonalId: item.PersonalAssigned.id,
                            RequestTypeId: item.Type.Id,
                            ClientId: item.Client?.Id,
                            ExpectedSolutionDateValue: new Date(
                              item.ExpectedSolutionDate
                            ),
                            EventId: item?.Event?.Id ?? 0,
                          });
                          setShowAddModal(true);
                        }}
                        hideDeleteButton
                      />
                    </div>,
                    <BackForeColor
                      backColor={item?.Type?.BackgroundColor}
                      foreColor={item?.Type?.ForegroundColor}
                      text={item?.Type?.Name}
                    />,
                    <RequestStatus status={item.Status} />,
                    item.Event ? (
                      <Link
                        color="primary"
                        underline="always"
                        href="#"
                        onClick={() => {
                          window.open(
                            `/events/detail/${item.Event.Id}`,
                            "_blank"
                          );
                        }}
                      >{`${item.Event.Code}`}</Link>
                    ) : (
                      <ValueOrNoDataLabel />
                    ),
                    <ValueOrNoDataLabel data={item?.Client?.Name} />,
                    <p>{`${item?.PersonalAssigned?.SurName} ${item?.PersonalAssigned?.LastName}`}</p>,
                    GetDateInDDMMYYYYFormat(
                      new Date(item?.ExpectedSolutionDate)
                    ),
                    new Date(item?.CreationDate).toLocaleString(undefined, {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }),
                  ],
                }))
              : [generateNoDataValues(9)]
          }
        />
      </Box>
    );
  };

  useEffect(() => {
    getMyRequests();
  }, [filters]);

  return isLoading ? <LoadingPanel donotUseHscreen /> : showUI();
}
